// Cleaning for HTML pasted into the signature editor.
//
// Pasted HTML goes into a live contenteditable, where an `<img onerror>` WOULD run,
// so it is cleaned before insertion. DOMParser builds an inert document: nothing in
// it loads or executes while we walk it. The server cleans again on save
// (backend/src/signature/html.ts); this copy exists for what happens before then.

const ALLOWED = new Set([
  "a", "b", "big", "blockquote", "br", "center", "col", "colgroup", "div", "em", "font",
  "h1", "h2", "h3", "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "s", "small",
  "span", "strike", "strong", "sub", "sup", "table", "tbody", "td", "tfoot", "th", "thead",
  "tr", "u", "ul",
]);
const DROP = new Set([
  "applet", "audio", "base", "button", "canvas", "embed", "form", "frame", "frameset",
  "head", "iframe", "input", "link", "math", "meta", "noscript", "object", "option",
  "script", "select", "source", "style", "svg", "template", "textarea", "title", "video", "xml",
]);
const COMMON = ["style", "dir", "align", "valign", "width", "height", "title", "lang"];
const PER_TAG: Record<string, string[]> = {
  a: ["href"],
  img: ["src", "alt", "border"],
  table: ["border", "cellpadding", "cellspacing", "bgcolor"],
  td: ["colspan", "rowspan", "bgcolor", "nowrap"],
  th: ["colspan", "rowspan", "bgcolor", "nowrap"],
  tr: ["bgcolor"],
  font: ["color", "face", "size"],
};
const BAD_STYLE = /expression|javascript|vbscript|url\s*\(|behavio|binding|@import|[<>\\]/i;
const scheme = (v: string) => v.replace(/[\u0000- ]+/g, "").toLowerCase();
const SAFE_HREF = /^(https?:|mailto:|tel:)/;
/** Images the pane can show and the server can store. `cid:`, `file:` and `blob:`
 *  references from another app's clipboard point at nothing here. */
const SAFE_SRC = /^(https?:|data:image\/(png|jpe?g|gif|webp|bmp);base64,)/;

/** Word puts paragraph formatting in a <style> block (`p.MsoNormal{…}`) rather than
 *  on the elements, so it is inlined before the block is dropped. */
function classStyles(doc: Document): Map<string, string> {
  const out = new Map<string, string>();
  for (const style of Array.from(doc.querySelectorAll("style"))) {
    let sheet: CSSStyleSheet | null = null;
    try {
      const s = new CSSStyleSheet();
      s.replaceSync(style.textContent ?? "");
      sheet = s;
    } catch {
      continue;
    }
    for (const rule of Array.from(sheet.cssRules)) {
      if (!(rule instanceof CSSStyleRule)) continue;
      for (const sel of rule.selectorText.split(",")) {
        const m = /^\s*([a-z0-9]{0,10})\.([A-Za-z0-9_-]{1,60})\s*$/i.exec(sel);
        if (!m) continue;
        const key = `${m[1].toLowerCase()}.${m[2]}`;
        out.set(key, [out.get(key), rule.style.cssText].filter(Boolean).join(";"));
      }
    }
  }
  return out;
}

export interface CleanResult {
  html: string;
  /** Images that could not come along (another app's private reference). */
  droppedImages: number;
}

export function cleanSignatureHtml(html: string): CleanResult {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const classes = classStyles(doc);
  let droppedImages = 0;

  const visit = (parent: Node) => {
    for (const node of Array.from(parent.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) continue;
      if (node.nodeType !== Node.ELEMENT_NODE) {
        node.remove();
        continue;
      }
      const el = node as Element;
      const name = el.localName.toLowerCase();
      if (DROP.has(name)) {
        el.remove();
        continue;
      }
      visit(el);
      const deadLink = name === "a" && !SAFE_HREF.test(scheme(el.getAttribute("href") ?? ""));
      if (!ALLOWED.has(name) || deadLink) {
        el.replaceWith(...Array.from(el.childNodes));
        continue;
      }
      if (name === "img" && !SAFE_SRC.test(scheme(el.getAttribute("src") ?? ""))) {
        droppedImages++;
        el.remove();
        continue;
      }
      const fromClass = Array.from(el.classList)
        .flatMap((c) => [classes.get(`.${c}`), classes.get(`${name}.${c}`)])
        .filter(Boolean)
        .join(";");
      const style = [fromClass, el.getAttribute("style") ?? ""].filter(Boolean).join(";");
      const keep = new Set([...COMMON, ...(PER_TAG[name] ?? [])]);
      for (const attr of Array.from(el.attributes)) {
        if (!keep.has(attr.name.toLowerCase())) el.removeAttribute(attr.name);
      }
      if (style && !BAD_STYLE.test(style)) el.setAttribute("style", style);
      else el.removeAttribute("style");
    }
  };
  visit(doc.body);
  return { html: doc.body.innerHTML.trim(), droppedImages };
}

export const escapeText = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\r?\n/g, "<br>");

export const MAX_IMAGE_BYTES = 400 * 1024;

export function readImageFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}
